import { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { AppShell } from "../components/AppShell";
import { ProtectedRoute } from "../components/ProtectedRoute";
import { PageLoadingFallback } from "../components/PageLoadingFallback";

import { useAuth } from "../context/AuthContext";
import { ScopeProvider } from "../context/ScopeContext";
import { FamilyScopeProvider } from "../context/FamilyScopeContext";
import { LoginPage } from "../pages/LoginPage";
import { NOTIFICATION_DELIVERY_PERMISSIONS } from "../services/notification.api";
import type { Permission } from "../types/domain";

// Lazy-loaded pages for route code-splitting
const SelectionApprovalPage = lazy(() => import("../pages/spmb/SelectionApprovalPage").then(m => ({ default: m.SelectionApprovalPage })));
const SelectionReleasePage = lazy(() => import("../pages/spmb/SelectionReleasePage").then(m => ({ default: m.SelectionReleasePage })));
const DashboardPage = lazy(() => import("../pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const EmployeePortalPage = lazy(() => import("../pages/EmployeePortalPage").then(m => ({ default: m.EmployeePortalPage })));
const IntegrationsPage = lazy(() => import("../pages/IntegrationsPage").then(m => ({ default: m.IntegrationsPage })));
const NotFoundPage = lazy(() => import("../pages/NotFoundPage").then(m => ({ default: m.NotFoundPage })));
const ParentPortalPage = lazy(() => import("../pages/ParentPortalPage").then(m => ({ default: m.ParentPortalPage })));
const PublicLandingPage = lazy(() => import("../pages/PublicLandingPage").then(m => ({ default: m.PublicLandingPage })));
const PublicRegistrationPage = lazy(() => import("../pages/PublicRegistrationPage").then(m => ({ default: m.PublicRegistrationPage })));
const PublicPaymentPage = lazy(() => import("../pages/PublicPaymentPage").then(m => ({ default: m.PublicPaymentPage })));
const StudentsPage = lazy(() => import("../pages/students/StudentsPage").then(m => ({ default: m.StudentsPage })));
const StudentAddPage = lazy(() => import("../pages/students/StudentAddPage").then(m => ({ default: m.StudentAddPage })));
const StudentProfilePage = lazy(() => import("../pages/students/profile/StudentProfilePage").then(m => ({ default: m.StudentProfilePage })));
const ImportStudentsPage = lazy(() => import("../pages/students/ImportStudentsPage").then(m => ({ default: m.ImportStudentsPage })));
const MenuManagementPage = lazy(() => import("../pages/access/MenuManagementPage").then(m => ({ default: m.MenuManagementPage })));
const RoleManagementPage = lazy(() => import("../pages/access/RoleManagementPage").then(m => ({ default: m.RoleManagementPage })));
const UserManagementPage = lazy(() => import("../pages/access/UserManagementPage").then(m => ({ default: m.UserManagementPage })));
const BroadcastCategoriesPage = lazy(() => import("../pages/broadcast/BroadcastCategoriesPage").then(m => ({ default: m.BroadcastCategoriesPage })));
const BroadcastTemplatesPage = lazy(() => import("../pages/broadcast/BroadcastTemplatesPage").then(m => ({ default: m.BroadcastTemplatesPage })));
const BroadcastSendPage = lazy(() => import("../pages/broadcast/BroadcastSendPage").then(m => ({ default: m.BroadcastSendPage })));
const BroadcastHistoryPage = lazy(() => import("../pages/broadcast/BroadcastHistoryPage").then(m => ({ default: m.BroadcastHistoryPage })));
const BroadcastDetailPage = lazy(() => import("../pages/broadcast/BroadcastDetailPage").then(m => ({ default: m.BroadcastDetailPage })));
const SettingsLayout = lazy(() => import("../pages/settings/SettingsLayout").then(m => ({ default: m.SettingsLayout })));
const SettingsGroupPage = lazy(() => import("../pages/settings/SettingsGroupPage").then(m => ({ default: m.SettingsGroupPage })));
const FoundationsPage = lazy(() => import("../pages/organization/FoundationsPage").then(m => ({ default: m.FoundationsPage })));
const SchoolUnitsPage = lazy(() => import("../pages/organization/SchoolUnitsPage").then(m => ({ default: m.SchoolUnitsPage })));
const AcademicYearsPage = lazy(() => import("../pages/organization/AcademicYearsPage").then(m => ({ default: m.AcademicYearsPage })));
const AuditHistoryPage = lazy(() => import("../pages/AuditHistoryPage").then(m => ({ default: m.AuditHistoryPage })));
const CmsLandingPage = lazy(() => import("../pages/cms/CmsLandingPage").then(m => ({ default: m.CmsLandingPage })));
const CmsTestimonialsPage = lazy(() => import("../pages/cms/CmsTestimonialsPage").then(m => ({ default: m.CmsTestimonialsPage })));
const CmsUnitProfilesPage = lazy(() => import("../pages/cms/CmsUnitProfilesPage").then(m => ({ default: m.CmsUnitProfilesPage })));

const AdmissionsConfigLayout = lazy(() => import("../pages/spmb/admissions-configurations").then(m => ({ default: m.AdmissionsConfigLayout })));
const ConfigurationsPage = lazy(() => import("../pages/spmb/admissions-configurations").then(m => ({ default: m.ConfigurationsPage })));
const PeriodsPage = lazy(() => import("../pages/spmb/admissions-configurations").then(m => ({ default: m.PeriodsPage })));
const WavesPage = lazy(() => import("../pages/spmb/admissions-configurations").then(m => ({ default: m.WavesPage })));
const TargetsPage = lazy(() => import("../pages/spmb/admissions-configurations").then(m => ({ default: m.TargetsPage })));
const SequencesPage = lazy(() => import("../pages/spmb/admissions-configurations").then(m => ({ default: m.SequencesPage })));
const TracksPage = lazy(() => import("../pages/spmb/admissions-configurations").then(m => ({ default: m.TracksPage })));
const ProgramsPage = lazy(() => import("../pages/spmb/admissions-configurations").then(m => ({ default: m.ProgramsPage })));
const SourcesPage = lazy(() => import("../pages/spmb/admissions-configurations").then(m => ({ default: m.SourcesPage })));
const BillingTypesTab = lazy(() => import("../pages/spmb/admissions-configurations").then(m => ({ default: m.BillingTypesTab })));
const BillingConfigurationsTab = lazy(() => import("../pages/spmb/admissions-configurations").then(m => ({ default: m.BillingConfigurationsTab })));
const SchedulingPoliciesTab = lazy(() => import("../pages/spmb/admissions-configurations").then(m => ({ default: m.SchedulingPoliciesTab })));
const ReRegistrationPoliciesTab = lazy(() => import("../pages/spmb/admissions-configurations").then(m => ({ default: m.ReRegistrationPoliciesTab })));

const BillingCategoriesPage = lazy(() => import("../pages/finance/billing/BillingCategoriesPage").then(m => ({ default: m.BillingCategoriesPage })));
const BillingTypesPage = lazy(() => import("../pages/finance/billing/BillingTypesPage").then(m => ({ default: m.BillingTypesPage })));
const BillingConfigurationsPage = lazy(() => import("../pages/finance/billing/BillingConfigurationsPage").then(m => ({ default: m.BillingConfigurationsPage })));
const PaymentPlansPage = lazy(() => import("../pages/finance/billing/PaymentPlansPage").then(m => ({ default: m.PaymentPlansPage })));
const DiscountsPage = lazy(() => import("../pages/finance/billing/DiscountsPage").then(m => ({ default: m.DiscountsPage })));
const ChartOfAccountsPage = lazy(() => import("../pages/accounting/ChartOfAccountsPage").then(m => ({ default: m.ChartOfAccountsPage })));
const FiscalPeriodsPage = lazy(() => import("../pages/accounting/FiscalPeriodsPage").then(m => ({ default: m.FiscalPeriodsPage })));
const OpeningBalancePage = lazy(() => import("../pages/accounting/OpeningBalancePage").then(m => ({ default: m.OpeningBalancePage })));
const JournalsPage = lazy(() => import("../pages/accounting/JournalsPage").then(m => ({ default: m.JournalsPage })));
const JournalMappingPage = lazy(() => import("../pages/accounting/JournalMappingPage").then(m => ({ default: m.JournalMappingPage })));
const GeneralLedgerPage = lazy(() => import("../pages/accounting/GeneralLedgerPage").then(m => ({ default: m.GeneralLedgerPage })));
const FinancialReportsPage = lazy(() => import("../pages/accounting/FinancialReportsPage").then(m => ({ default: m.FinancialReportsPage })));
const PaymentSimulatorPage = lazy(() => import("../pages/sandbox/PaymentSimulatorPage").then(m => ({ default: m.PaymentSimulatorPage })));
const DocumentRequirementsConfigPage = lazy(() => import("../pages/spmb/DocumentRequirementsConfigPage").then(m => ({ default: m.DocumentRequirementsConfigPage })));
const ObservationFormConfigPage = lazy(() => import("../pages/spmb/ObservationFormConfigPage").then(m => ({ default: m.ObservationFormConfigPage })));
const AssistedFamilyRegistrationPage = lazy(() => import("../pages/spmb/AssistedFamilyRegistrationPage").then(m => ({ default: m.AssistedFamilyRegistrationPage })));
const InvoicesPage = lazy(() => import("../pages/finance/InvoicesPage").then(m => ({ default: m.InvoicesPage })));
const FormFeeReconciliationPage = lazy(() => import("../pages/finance/FormFeeReconciliationPage").then(m => ({ default: m.FormFeeReconciliationPage })));
const EntranceFeeDispositionsPage = lazy(() => import("../pages/finance/EntranceFeeDispositionsPage").then(m => ({ default: m.EntranceFeeDispositionsPage })));
const ReRegistrationDiscountReportPage = lazy(() => import("../pages/finance/reports/ReRegistrationDiscountReportPage").then(m => ({ default: m.ReRegistrationDiscountReportPage })));
const PhysicalDocumentChecklistPage = lazy(() => import("../pages/spmb/PhysicalDocumentChecklistPage").then(m => ({ default: m.PhysicalDocumentChecklistPage })));
const ObserverRosterPage = lazy(() => import("../pages/spmb/ObserverRosterPage").then(m => ({ default: m.ObserverRosterPage })));
const ObserverAssessmentPage = lazy(() => import("../pages/spmb/ObserverAssessmentPage").then(m => ({ default: m.ObserverAssessmentPage })));
const InterviewObservationsPage = lazy(() => import("../pages/spmb/InterviewObservationsPage").then(m => ({ default: m.InterviewObservationsPage })));
const ObserversManagementPage = lazy(() => import("../pages/spmb/ObserversManagementPage").then(m => ({ default: m.ObserversManagementPage })));
const SchedulingAdminPage = lazy(() => import("../pages/spmb/SchedulingAdminPage").then(m => ({ default: m.SchedulingAdminPage })));
const SpmbDashboardPage = lazy(() => import("../pages/spmb/SpmbDashboardPage").then(m => ({ default: m.SpmbDashboardPage })));
const RegistrationPage = lazy(() => import("../pages/spmb/RegistrationPage").then(m => ({ default: m.RegistrationPage })));
const ApplicantProfilePage = lazy(() => import("../pages/spmb/profile/ApplicantProfilePage").then(m => ({ default: m.ApplicantProfilePage })));
const DataExportsPage = lazy(() => import("../pages/spmb/DataExportsPage").then(m => ({ default: m.DataExportsPage })));
const StudentConversionPage = lazy(() => import("../pages/spmb/StudentConversionPage").then(m => ({ default: m.StudentConversionPage })));
const DocumentVerificationPage = lazy(() => import("../pages/spmb/DocumentVerificationPage").then(m => ({ default: m.DocumentVerificationPage })));
const NotificationDeliveriesPage = lazy(() => import("../pages/spmb/NotificationDeliveriesPage").then(m => ({ default: m.NotificationDeliveriesPage })));
const NotificationTemplatesPage = lazy(() => import("../pages/spmb/NotificationTemplatesPage").then(m => ({ default: m.NotificationTemplatesPage })));
const ReferralDashboardPage = lazy(() => import("../pages/referral/ReferralDashboardPage").then(m => ({ default: m.ReferralDashboardPage })));
const ReferralPartnersPage = lazy(() => import("../pages/referral/ReferralPartnersPage").then(m => ({ default: m.ReferralPartnersPage })));
const ReferralAttributionsPage = lazy(() => import("../pages/referral/ReferralAttributionsPage").then(m => ({ default: m.ReferralAttributionsPage })));
const ReferralCommissionsPage = lazy(() => import("../pages/referral/ReferralCommissionsPage").then(m => ({ default: m.ReferralCommissionsPage })));
const ReferralRulesPage = lazy(() => import("../pages/referral/ReferralRulesPage").then(m => ({ default: m.ReferralRulesPage })));
const PublicationMediaPage = lazy(() => import("../pages/referral/PublicationMediaPage").then(m => ({ default: m.PublicationMediaPage })));
const PartnerHomePage = lazy(() => import("../pages/mitra/PartnerHomePage").then(m => ({ default: m.PartnerHomePage })));
const PartnerRegistrantsPage = lazy(() => import("../pages/mitra/PartnerRegistrantsPage").then(m => ({ default: m.PartnerRegistrantsPage })));
const PartnerCommissionsPage = lazy(() => import("../pages/mitra/PartnerCommissionsPage").then(m => ({ default: m.PartnerCommissionsPage })));
const PartnerProfilePage = lazy(() => import("../pages/mitra/PartnerProfilePage").then(m => ({ default: m.PartnerProfilePage })));
const PartnerMediaPage = lazy(() => import("../pages/mitra/PartnerMediaPage").then(m => ({ default: m.PartnerMediaPage })));
const BankFilePage = lazy(() => import("../pages/document-template/BankFilePage").then(m => ({ default: m.BankFilePage })));
const TemplateCategoriesPage = lazy(() => import("../pages/document-template/TemplateCategoriesPage").then(m => ({ default: m.TemplateCategoriesPage })));
// Bantuan tidak dibungkus SecuredPage: server yang menyaring panduan per permission.
const HelpIndexPage = lazy(() => import("../features/help/pages/HelpIndexPage").then(m => ({ default: m.HelpIndexPage })));
const HelpPage = lazy(() => import("../features/help/pages/HelpPage").then(m => ({ default: m.HelpPage })));
const HelpLayout = lazy(() => import("../features/help/pages/HelpLayout").then(m => ({ default: m.HelpLayout })));
const ExecutiveDashboardPage = lazy(() => import("../pages/executive/ExecutiveDashboardPage").then(m => ({ default: m.ExecutiveDashboardPage })));

function SpmbApplicantsRedirect() {
  const location = useLocation();
  return <Navigate to={`/app/spmb/registrations${location.search}`} replace />;
}

export function FormFeeInvoicesRedirect() {
  const { hasAnyPermission, user, getHomePath } = useAuth();
  const location = useLocation();

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  const hasFinanceAccess = hasAnyPermission(
    "finance.form-fee.reconcile",
    "finance.form-fee.correction.make",
    "finance.form-fee.correction.check",
    "finance.form-fee.invoice.read",
  );

  if (hasFinanceAccess) {
    return (
      <Navigate
        to={`/app/finance/form-fee-reconciliation${location.search}`}
        replace
      />
    );
  }

  const hasSpmbAccess = hasAnyPermission(
    "spmb.applicants.read",
    "spmb.reporting.read",
  );

  if (hasSpmbAccess) {
    return <Navigate to={`/app/spmb/registrations${location.search}`} replace />;
  }

  return <Navigate to={getHomePath(user.role)} replace />;
}

/**
 * Any of these opens the settings shell; which groups appear inside it is
 * decided by the server from the same permissions, not repeated here.
 */
const SETTINGS_PERMISSIONS: Permission[] = [
  "settings.manage",
  "settings.view",
  "settings.organization.view",
  "settings.access.view",
  "settings.communication.view",
  "settings.integration.view",
  "settings.system.view",
];

function SecuredPage({
  permission,
  children,
}: {
  /** Beberapa permission berarti "mana saja boleh". */
  permission: Permission | Permission[];
  children: React.ReactNode;
}) {
  return <ProtectedRoute permission={permission}>{children}</ProtectedRoute>;
}

function PortalKeluargaRedirect() {
  const location = useLocation();
  return <Navigate to={`/app/parent${location.search}`} replace />;
}

function LupaPasswordRedirect() {
  const location = useLocation();
  return <Navigate to={`/forgot-password${location.search}`} replace />;
}

export default function App() {
  return (
    <Suspense fallback={<PageLoadingFallback />}>
      <Routes>
      <Route path="/" element={<PublicLandingPage />} />
      <Route path="/spmb/daftar" element={<PublicRegistrationPage />} />
      <Route path="/spmb/payment/:regId" element={<PublicPaymentPage />} />
      <Route path="/daftar" element={<PublicRegistrationPage />} />
      <Route path="/portal/keluarga" element={<PortalKeluargaRedirect />} />
      <Route path="/parent" element={<PortalKeluargaRedirect />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<LoginPage />} />
      <Route path="/lupa-password" element={<LupaPasswordRedirect />} />

      {/* Bantuan memakai tata letak dokumentasi sendiri, di luar AppShell. */}
      {["/app/bantuan", "/mitra/bantuan"].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute>
              <HelpLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<HelpIndexPage />} />
          <Route path=":module/:slug" element={<HelpPage />} />
        </Route>
      ))}

      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <ScopeProvider>
              <FamilyScopeProvider>
                <AppShell />
              </FamilyScopeProvider>
            </ScopeProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="dashboard" element={<Navigate to="/app" replace />} />

        <Route
          path="executive"
          element={
            <SecuredPage permission="dashboard.executive.view">
              <ExecutiveDashboardPage />
            </SecuredPage>
          }
        />

        <Route
          path="spmb/dashboard"
          element={
            <SecuredPage permission="spmb.reporting.read">
              <SpmbDashboardPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/registrations"
          element={
            <SecuredPage permission="spmb.applicants.read">
              <RegistrationPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/registrations/:id"
          element={
            <SecuredPage permission="spmb.applicants.read">
              <ApplicantProfilePage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/applicants"
          element={<SpmbApplicantsRedirect />}
        />
        <Route
          path="spmb/applicants/:id"
          element={
            <SecuredPage permission="spmb.applicants.read">
              <ApplicantProfilePage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/exports"
          element={
            <SecuredPage permission="spmb.reporting.read">
              <DataExportsPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/documents"
          element={
            <SecuredPage
              permission={[
                "spmb.physical-documents.read",
                "spmb.applicants.read",
              ]}
            >
              <PhysicalDocumentChecklistPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/digital-documents"
          element={
            <SecuredPage permission="spmb.applicants.read">
              <DocumentVerificationPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/selection"
          element={
            <SecuredPage permission="spmb.selection.approve">
              <SelectionApprovalPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/announcements"
          element={
            <SecuredPage permission="spmb.selection.release">
              <SelectionReleasePage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/student-conversion"
          element={
            <SecuredPage permission="spmb.applicants.read">
              <StudentConversionPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/admissions-configurations"
          element={
            <SecuredPage
              permission={[
                'spmb.periods.read',
                'spmb.billing.read',
                'finance.billing.read',
                'spmb.scheduling.read',
                'spmb.scheduling.manage',
              ]}
            >
              <AdmissionsConfigLayout />
            </SecuredPage>
          }
        >
          <Route index element={<Navigate to="configurations" replace />} />
          <Route path="configurations" element={<ConfigurationsPage />} />
          <Route path="periods" element={<PeriodsPage />} />
          <Route path="waves" element={<WavesPage />} />
          <Route path="targets" element={<TargetsPage />} />
          <Route path="sequences" element={<SequencesPage />} />
          <Route path="tracks" element={<TracksPage />} />
          <Route path="programs" element={<ProgramsPage />} />
          <Route path="sources" element={<SourcesPage />} />
          <Route path="billing-types" element={<BillingTypesTab />} />
          <Route path="billing-configurations" element={<BillingConfigurationsTab />} />
          <Route path="payment-plans" element={<PaymentPlansPage readOnly />} />
          <Route path="scheduling" element={<SchedulingPoliciesTab />} />
          <Route path="re-registration" element={<ReRegistrationPoliciesTab />} />
        </Route>
        <Route
          path="spmb/document-requirements"
          element={
            <SecuredPage permission="spmb.periods.read">
              <DocumentRequirementsConfigPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/observation-forms"
          element={
            <SecuredPage permission="spmb.periods.read">
              <ObservationFormConfigPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/bank-file"
          element={
            <SecuredPage
              permission={[
                'spmb.bank-file.read',
                'document-template.templates.view',
              ]}
            >
              <BankFilePage
                forcedCategory="SPMB"
                title="Bank File SPMB"
                description="Kelola master file PDF atau gambar formulir resmi, blanko surat, brosur, dan template dokumen khusus penerimaan siswa baru (SPMB)."
              />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/family-assistance"
          element={
            <SecuredPage permission="spmb.families.assist">
              <AssistedFamilyRegistrationPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/scheduling"
          element={
            <SecuredPage permission="spmb.scheduling.read">
              <SchedulingAdminPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/observers"
          element={
            <SecuredPage permission="spmb.observers.manage">
              <ObserversManagementPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/observer-roster"
          element={
            <SecuredPage permission="spmb.observers.roster">
              <ObserverRosterPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/observer-assessments/:applicationId"
          element={
            <SecuredPage permission="spmb.assessments.read">
              <ObserverAssessmentPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/observer-roster/:applicationId/assessment"
          element={
            <SecuredPage permission="spmb.assessments.read">
              <ObserverAssessmentPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/interview-observations"
          element={
            <SecuredPage
              permission={["spmb.assessments.read", "spmb.observers.roster"]}
            >
              <InterviewObservationsPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/interviews"
          element={
            <SecuredPage
              permission={["spmb.assessments.read", "spmb.observers.roster"]}
            >
              <InterviewObservationsPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/interview"
          element={
            <SecuredPage
              permission={["spmb.assessments.read", "spmb.observers.roster"]}
            >
              <InterviewObservationsPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/notification-deliveries"
          element={
            <SecuredPage permission={[...NOTIFICATION_DELIVERY_PERMISSIONS]}>
              <NotificationDeliveriesPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/notification-templates"
          element={
            <SecuredPage permission="notification.templates.read">
              <NotificationTemplatesPage />
            </SecuredPage>
          }
        />

        <Route
          path="referral/dashboard"
          element={
            <SecuredPage permission="referral.attributions.read">
              <ReferralDashboardPage />
            </SecuredPage>
          }
        />
        <Route
          path="referral/partners"
          element={
            <SecuredPage permission="referral.partners.read">
              <ReferralPartnersPage />
            </SecuredPage>
          }
        />
        <Route
          path="referral/attributions"
          element={
            <SecuredPage permission="referral.attributions.read">
              <ReferralAttributionsPage />
            </SecuredPage>
          }
        />
        <Route
          path="referral/commissions"
          element={
            <SecuredPage permission="referral.commissions.read">
              <ReferralCommissionsPage />
            </SecuredPage>
          }
        />
        {/*
          Aturan komisi adalah konfigurasi bisnis, jadi menunya berada di grup
          Pengaturan. Halamannya tetap berdiri sendiri karena datanya berupa
          tabel aturan, bukan pasangan kunci-nilai seperti pengaturan lain.
        */}
        <Route
          path="referral/rules"
          element={
            <SecuredPage
              permission={["referral.rules.read", "referral.rules.write"]}
            >
              <ReferralRulesPage />
            </SecuredPage>
          }
        />
        <Route
          path="referral/media-publikasi"
          element={
            <SecuredPage permission="referral.publication-media.read">
              <PublicationMediaPage />
            </SecuredPage>
          }
        />

        <Route
          path="students"
          element={
            <SecuredPage permission="finance.students.read">
              <StudentsPage />
            </SecuredPage>
          }
        />
        <Route
          path="students/add"
          element={
            <SecuredPage permission="finance.students.write">
              <StudentAddPage />
            </SecuredPage>
          }
        />
        <Route
          path="students/:id"
          element={
            <SecuredPage permission="finance.students.read">
              <StudentProfilePage />
            </SecuredPage>
          }
        />
        <Route
          path="students/import"
          element={
            <SecuredPage permission="finance.students.write">
              <ImportStudentsPage />
            </SecuredPage>
          }
        />
          <Route
            path="finance/invoices"
            element={
              <SecuredPage permission={["finance.invoices.read", "finance.payments.write", "finance.payments.verify"]}>
                <InvoicesPage />
              </SecuredPage>
            }
          />
          <Route
            path="finance/form-fee-reconciliation"
            element={
              <SecuredPage
                permission={[
                  "finance.form-fee.reconcile",
                  "finance.form-fee.correction.make",
                  "finance.form-fee.correction.check",
                ]}
              >
                <FormFeeReconciliationPage />
              </SecuredPage>
            }
          />
          <Route
            path="finance/reconciliations/form-fee"
            element={<Navigate to="/app/finance/form-fee-reconciliation" replace />}
          />
          <Route
            path="finance/form-fee-invoices"
            element={<FormFeeInvoicesRedirect />}
          />
          <Route
            path="finance/entrance-fee-dispositions"
            element={
              <SecuredPage
                permission={[
                  "finance.entrance-fee.disposition.read",
                  "finance.entrance-fee.disposition.write",
                  "finance.entrance-fee.disposition.settle",
                ]}
              >
                <EntranceFeeDispositionsPage />
              </SecuredPage>
            }
          />

          {/* Accounting Module */}
          <Route path="accounting/accounts" element={<SecuredPage permission="accounting.accounts.read"><ChartOfAccountsPage /></SecuredPage>} />
          <Route path="accounting/periods" element={<SecuredPage permission="accounting.periods.read"><FiscalPeriodsPage /></SecuredPage>} />
          <Route path="accounting/opening-balance" element={<SecuredPage permission="accounting.opening-balance.read"><OpeningBalancePage /></SecuredPage>} />
          <Route path="accounting/journals" element={<SecuredPage permission="accounting.journals.read"><JournalsPage /></SecuredPage>} />
          <Route path="accounting/journal-mapping" element={<SecuredPage permission="accounting.mapping.read"><JournalMappingPage /></SecuredPage>} />
          <Route path="accounting/general-ledger" element={<SecuredPage permission="accounting.ledger.read"><GeneralLedgerPage /></SecuredPage>} />
          <Route path="accounting/reports" element={<SecuredPage permission="accounting.reports.read"><FinancialReportsPage /></SecuredPage>} />
          <Route path="sandbox/payment-simulator" element={<PaymentSimulatorPage />} />
        <Route
          path="finance/payment-plans"
          element={
            <SecuredPage
              permission={['finance.billing.read', 'spmb.billing.read']}
            >
              <PaymentPlansPage />
            </SecuredPage>
          }
        />
        <Route
          path="finance/spmb/payment-plans"
          element={<Navigate to="/app/finance/payment-plans" replace />}
        />
        <Route
          path="finance/discounts"
          element={
            <SecuredPage
              permission={['finance.billing.read', 'spmb.billing.read']}
            >
              <DiscountsPage />
            </SecuredPage>
          }
        />
        <Route
          path="finance/spmb/discounts"
          element={<Navigate to="/app/finance/discounts" replace />}
        />
        <Route
          path="finance/billing-categories"
          element={
            <SecuredPage
              permission={['finance.billing.read', 'spmb.billing.read']}
            >
              <BillingCategoriesPage />
            </SecuredPage>
          }
        />
        <Route
          path="finance/billing-types"
          element={
            <SecuredPage
              permission={['finance.billing.read', 'spmb.billing.read']}
            >
              <BillingTypesPage />
            </SecuredPage>
          }
        />
        <Route
          path="finance/billing-configurations"
          element={
            <SecuredPage
              permission={['finance.billing.read', 'spmb.billing.read']}
            >
              <BillingConfigurationsPage />
            </SecuredPage>
          }
        />
        <Route
          path="finance/reports/re-registration-discounts"
          element={
            <SecuredPage
              permission={[
                'finance.billing.read',
                'spmb.reporting.read',
                'spmb.applicants.read',
              ]}
            >
              <ReRegistrationDiscountReportPage />
            </SecuredPage>
          }
        />
        <Route
          path="spmb/reports/re-registration-discounts"
          element={
            <SecuredPage
              permission={[
                'spmb.reporting.read',
                'spmb.applicants.read',
                'finance.billing.read',
              ]}
            >
              <ReRegistrationDiscountReportPage />
            </SecuredPage>
          }
        />

        <Route
          path="parent"
          element={
            <SecuredPage permission="spmb.families.read">
              <ParentPortalPage />
            </SecuredPage>
          }
        />
        <Route
          path="portal/keluarga"
          element={<PortalKeluargaRedirect />}
        />
        <Route
          path="employee"
          element={
            <SecuredPage permission="employee.portal">
              <EmployeePortalPage />
            </SecuredPage>
          }
        />

        <Route
          path="access/users"
          element={
            <SecuredPage permission="iam.users.read">
              <UserManagementPage />
            </SecuredPage>
          }
        />
        <Route
          path="access/roles"
          element={
            <SecuredPage permission="access.manage">
              <RoleManagementPage />
            </SecuredPage>
          }
        />
        <Route
          path="access/menus"
          element={
            <SecuredPage permission="access.manage">
              <MenuManagementPage />
            </SecuredPage>
          }
        />
        <Route
          path="audit"
          element={
            <SecuredPage permission="audit.read">
              <AuditHistoryPage />
            </SecuredPage>
          }
        />
        <Route
          path="cms/landing"
          element={
            <SecuredPage permission="cms.landing.read">
              <CmsLandingPage />
            </SecuredPage>
          }
        />
        <Route
          path="cms/unit-profiles"
          element={
            <SecuredPage permission="cms.unit-profiles.read">
              <CmsUnitProfilesPage />
            </SecuredPage>
          }
        />
        <Route
          path="cms/testimonials"
          element={
            <SecuredPage permission="cms.testimonials.read">
              <CmsTestimonialsPage />
            </SecuredPage>
          }
        />

        <Route
          path="organization/foundations"
          element={
            <SecuredPage permission="organization.foundations.view">
              <FoundationsPage />
            </SecuredPage>
          }
        />
        <Route
          path="organization/units"
          element={
            <SecuredPage permission="organization.school-units.view">
              <SchoolUnitsPage />
            </SecuredPage>
          }
        />
        <Route
          path="organization/academic-years"
          element={
            <SecuredPage permission="organization.academic-years.view">
              <AcademicYearsPage />
            </SecuredPage>
          }
        />

        <Route
          path="broadcast/categories"
          element={
            <SecuredPage permission="broadcast.categories.view">
              <BroadcastCategoriesPage />
            </SecuredPage>
          }
        />
        <Route
          path="broadcast/templates"
          element={
            <SecuredPage permission="broadcast.templates.view">
              <BroadcastTemplatesPage />
            </SecuredPage>
          }
        />
        <Route
          path="broadcast/send"
          element={
            <SecuredPage permission="broadcast.send.send">
              <BroadcastSendPage />
            </SecuredPage>
          }
        />
        <Route
          path="broadcast/history"
          element={
            <SecuredPage permission="broadcast.history.view">
              <BroadcastHistoryPage />
            </SecuredPage>
          }
        />
        <Route
          path="broadcast/history/:id"
          element={
            <SecuredPage permission="broadcast.history.view">
              <BroadcastDetailPage />
            </SecuredPage>
          }
        />

        <Route
          path="bank-file"
          element={
            <SecuredPage permission="document-template.templates.view">
              <BankFilePage />
            </SecuredPage>
          }
        />
        <Route
          path="bank-file/categories"
          element={
            <SecuredPage permission="document-template.categories.read">
              <TemplateCategoriesPage />
            </SecuredPage>
          }
        />

        <Route
          path="settings/integrations"
          element={
            <SecuredPage permission={SETTINGS_PERMISSIONS}>
              <IntegrationsPage />
            </SecuredPage>
          }
        />
        {/*
          Alamat lama halaman pengaturan. Dipertahankan agar tautan tersimpan
          dan riwayat peramban tidak putus; tujuannya sekarang grup pertama
          yang berhak diakses pengguna.
        */}
        <Route
          path="mitra"
          element={
            <SecuredPage permission="referral.partner.self">
              <PartnerHomePage />
            </SecuredPage>
          }
        />
        <Route
          path="mitra/pendaftar"
          element={
            <SecuredPage permission="referral.partner.self">
              <PartnerRegistrantsPage />
            </SecuredPage>
          }
        />
        <Route
          path="mitra/komisi"
          element={
            <SecuredPage permission="referral.partner.self">
              <PartnerCommissionsPage />
            </SecuredPage>
          }
        />
        <Route
          path="mitra/profil"
          element={
            <SecuredPage permission="referral.partner.self">
              <PartnerProfilePage />
            </SecuredPage>
          }
        />
        <Route
          path="mitra/media"
          element={
            <SecuredPage permission="referral.partner.self">
              <PartnerMediaPage />
            </SecuredPage>
          }
        />
        <Route
          path="settings/general"
          element={<Navigate to="/app/settings" replace />}
        />
        <Route
          path="settings"
          element={
            <SecuredPage permission={SETTINGS_PERMISSIONS}>
              <SettingsLayout />
            </SecuredPage>
          }
        >
          <Route path=":group" element={<SettingsGroupPage />} />
        </Route>
      </Route>

      <Route
        path="/mitra"
        element={
          <ProtectedRoute permission="referral.partner.self">
            <ScopeProvider>
              <FamilyScopeProvider>
                <AppShell />
              </FamilyScopeProvider>
            </ScopeProvider>
          </ProtectedRoute>
        }
      >
        <Route index element={<PartnerHomePage />} />
        <Route path="pendaftar" element={<PartnerRegistrantsPage />} />
        <Route path="komisi" element={<PartnerCommissionsPage />} />
        <Route path="profil" element={<PartnerProfilePage />} />
        <Route path="media" element={<PartnerMediaPage />} />
      </Route>

      <Route
        path="/finance/form-fee-invoices"
        element={<FormFeeInvoicesRedirect />}
      />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </Suspense>
  );
}
